import * as React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "../ui/TextField";
import { Select } from "../ui/Select";
import { Textarea } from "../ui/Textarea";
import { visibleSpecs } from "./bookingConfig";
import type { FieldErrors, FieldSpec, FormValues } from "./bookingConfig";

type Props = {
  specs: FieldSpec[];
  values: FormValues;
  errors: FieldErrors;
  onChange: (key: string, value: string) => void;
};

export function BookingFields({ specs, values, errors, onChange }: Props) {
  const { t } = useTranslation("booking");

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {visibleSpecs(specs, values).map((spec) => {
        const errorKey = errors[spec.key];
        const common = {
          label: t(spec.labelKey),
          value: values[spec.key] ?? "",
          onChange: (v: string) => onChange(spec.key, v),
          requirement: spec.requirement,
          error: errorKey ? t(errorKey) : undefined,
        };
        if (spec.kind === "select") {
          // Required selects with no explicit placeholder fall back to the
          // generic "Select…" prompt.
          const selectPlaceholder = spec.placeholderKey
            ? t(spec.placeholderKey)
            : spec.requirement === "required"
              ? t("selectPlaceholder")
              : undefined;
          const options = spec.optionKeys
            ? spec.optionKeys.map((o) => ({ value: o.value, label: t(o.labelKey) }))
            : (spec.options ?? []);
          return (
            <Select
              key={spec.key}
              {...common}
              options={options}
              searchable={spec.searchable}
              placeholder={selectPlaceholder}
            />
          );
        }
        if (spec.kind === "textarea") {
          // Textareas span both columns of the desktop grid.
          return (
            <div key={spec.key} className="md:col-span-2">
              <Textarea
                {...common}
                placeholder={spec.placeholderKey ? t(spec.placeholderKey) : undefined}
                maxLength={spec.maxLength}
              />
            </div>
          );
        }
        const field = (
          <TextField
            key={spec.key}
            {...common}
            type={spec.kind}
            placeholder={spec.placeholderKey ? t(spec.placeholderKey) : undefined}
          />
        );
        return spec.fullWidth ? (
          <div key={spec.key} className="md:col-span-2">
            {field}
          </div>
        ) : (
          field
        );
      })}
    </div>
  );
}
